import React from 'react';
import { Box, ButtonV2, DialogV2, DialogV2Props, Flex, FormControl, IconV2, InputV2, LinkV2, TextV2 } from '@withjoy/joykit';
import { Link } from '@withjoy/joykit/icons';
import { ReactComponent as Warning } from '@apps/registry/admin/assets/Warning.svg';
import { StyledBoxDivider, styles as dialogStyles } from '../AppPaymentPlatformSelect/AppPlatformSelect.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { DonationFundPlatformTypeEnum } from '@graphql/generated';
import { getPaymentMethodFormattedName } from '@shared/utils/paymentMethodOptions';
import { FormikProps } from 'formik';
import { useTranslation } from '@shared/core';
import { useAdminRegistryTelemetry } from '@apps/registry/admin/AdminRegistry.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';
import { getPaymentUsernameFormat } from '@apps/registry/common/components/AddCashFund/helpers';

interface AppPaymentDisclaimerDialogProps extends DialogV2Props {
  paymentMethod?: Maybe<DonationFundPlatformTypeEnum>;
  paymentLink?: string;
  paymentUsername?: string;
  formik: FormikProps<{ agreement: string }>;
  currencyCode?: string;
}

export const AppPaymentDisclaimerDialog: React.FC<AppPaymentDisclaimerDialogProps> = props => {
  const { t } = useTranslation('sharedRegistry');
  const translations = t('cashFundDisclaimerDialog');
  const { title, warning, paymentAccount, agreement, buttons } = translations;
  const telemetry = useAdminRegistryTelemetry();
  useMount(() => {
    if (props.isOpen) {
      telemetry.cashfundDisclaimerDialog.enter();
    }
  });
  return (
    <DialogV2
      {...props}
      size="lg"
      stickyBehavior="header"
    >
      <DialogV2.Header>
        <DialogV2.CloseButton />
      </DialogV2.Header>
      <DialogV2.Body>
        <Flex
          flexDirection="column"
          alignItems="center"
          paddingBottom={6}
        >
          <IconV2
            size="xxl"
            color="mono14"
          >
            <Warning />
          </IconV2>
          <TextV2
            marginY={5}
            typographyVariant="hed4"
            textAlign="center"
          >
            {title()}
          </TextV2>
          <TextV2 typographyVariant="body3">{warning.main()}</TextV2>
        </Flex>
        <StyledBoxDivider __css={dialogStyles.boxDividerStyles}>
          <TextV2 typographyVariant="body1">{warning.additional()}</TextV2>
        </StyledBoxDivider>
        <Flex
          flexDirection="column"
          marginBottom={5}
          alignItems="center"
        >
          <TextV2
            typographyVariant="body1"
            textAlign="center"
          >
            {paymentAccount.label()}
          </TextV2>
          <Flex alignItems="center">
            <TextV2
              color="linkText"
              textAlign="center"
              fontFamily="Inter"
              fontSize={pxToRem(24)}
              fontStyle="normal"
              fontWeight={600}
              lineHeight="130%"
              letterSpacing="-0.32px"
              as="a"
              href={props.paymentLink}
              target="_blank"
            >
              {getPaymentUsernameFormat(props.paymentMethod, props.currencyCode) || ''}
              {props.paymentUsername}
            </TextV2>
            <IconV2
              size="lg"
              color="linkText"
            >
              <Link />
            </IconV2>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          marginBottom={6}
          alignItems="center"
        >
          <TextV2
            typographyVariant="body1"
            color="#FF0000"
            textAlign="center"
          >
            {paymentAccount.test_warning({ paymentMethod: getPaymentMethodFormattedName(props.paymentMethod as string) })}
          </TextV2>
          <LinkV2 textAlign="center">
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >
              {paymentAccount.learn_more}
            </TextV2>
          </LinkV2>
        </Flex>
        <Box paddingY={5}>
          <TextV2 typographyVariant="body1">{agreement.instruction()}</TextV2>
        </Box>
        <form onSubmit={props.formik.handleSubmit}>
          <Box paddingY={5}>
            <FormControl
              label={agreement.label}
              error={props.formik.errors.agreement}
            >
              <InputV2
                id="acceptance"
                placeholder={agreement.placeholder()}
                name="agreement"
                value={props.formik.values.agreement}
                onChange={props.formik.handleChange}
              />
            </FormControl>
          </Box>
          <Flex
            flexDirection="column"
            alignItems="center"
            gap={5}
          >
            <ButtonV2
              type="submit"
              shape="rounded"
              intent="neutral"
              width="100%"
              disabled={!(props.formik.isValid && props.formik.dirty)}
            >
              {buttons.accept()}
            </ButtonV2>
            <ButtonV2
              intent="neutral"
              variant="ghost"
              onClick={e => {
                e.preventDefault();
                props.onClose?.({ closeSource: 'custom' });
              }}
            >
              {buttons.cancel()}
            </ButtonV2>
          </Flex>
        </form>
      </DialogV2.Body>
    </DialogV2>
  );
};
